import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Cancel, ContentCopy, CreditCard, OpenInNew, Refresh, Sync } from '@mui/icons-material';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { efiApi } from '@/services/api';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectBankAccount } from '@/store/slices/bankAccountSlice';

interface Cobranca {
  id: string;
  chargeId: number;
  customId: string | null;
  status: string;
  pago: boolean;
  finalizado: boolean;
  valor: number;
  paymentMethod: string | null;
  paymentUrl: string | null;
  descricao: string | null;
  expireAt: string | null;
  customer: any;
  payment: any;
  history: { id: number; current: string; previous: string | null; created_at: string; value?: number }[];
  notificationUrl: string | null;
  paidAt: string | null;
  lastNotificationAt: string | null;
  createdAt: string;
}

const brl = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmt = (v?: string | null) => {
  if (!v) return '—';
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v.split('-').reverse().join('/');
  try { return format(new Date(v), 'dd/MM/yyyy HH:mm'); } catch { return v; }
};

const STATUS_LABEL: Record<string, { label: string; color: 'default' | 'success' | 'warning' | 'error' | 'info' }> = {
  new: { label: 'Nova', color: 'default' },
  waiting: { label: 'Aguardando', color: 'warning' },
  link: { label: 'Link ativo', color: 'info' },
  identified: { label: 'Identificada', color: 'info' },
  approved: { label: 'Aprovada', color: 'success' },
  paid: { label: 'Paga', color: 'success' },
  settled: { label: 'Liquidada', color: 'success' },
  unpaid: { label: 'Não paga', color: 'error' },
  refunded: { label: 'Estornada', color: 'error' },
  contested: { label: 'Contestada', color: 'error' },
  canceled: { label: 'Cancelada', color: 'default' },
};

const METODO: Record<string, string> = { credit_card: 'Cartão de crédito', banking_billet: 'Boleto', all: 'Cartão ou boleto' };

const errMsg = (e: any, fallback: string) =>
  e?.response?.data?.error?.message || e?.error?.message || e?.message || fallback;

const copy = (text: string) => navigator.clipboard.writeText(text).then(() => toast.success('Link copiado'));

const EfiCobrancasPage = () => {
  const account = useAppSelector(selectBankAccount);
  const [items, setItems] = useState<Cobranca[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filtro, setFiltro] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ valor: '', descricao: '', paymentMethod: 'credit_card', validadeDias: '7', nome: '', documento: '', email: '' });
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState<Cobranca | null>(null);
  const [detail, setDetail] = useState<Cobranca | null>(null);

  const load = useCallback(async (refresh = false) => {
    refresh ? setRefreshing(true) : setLoading(true);
    try {
      const r = await efiApi.cobrancas({ refresh, limit: 200 });
      setItems(r.data || []);
    } catch (e: any) {
      toast.error(errMsg(e, 'Erro ao listar cobranças'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, account?.id]);

  const create = async () => {
    const valor = parseFloat(form.valor.replace(/\./g, '').replace(',', '.'));
    if (!Number.isFinite(valor) || valor < 1) return toast.error('Valor mínimo R$ 1,00');
    if (!form.descricao.trim()) return toast.error('Informe a descrição');
    setSaving(true);
    try {
      const r = await efiApi.criarCobrancaLink({
        valor, descricao: form.descricao.trim(), paymentMethod: form.paymentMethod as any,
        validadeDias: parseInt(form.validadeDias, 10) || 7,
        cliente: form.nome || form.documento || form.email ? { nome: form.nome, documento: form.documento, email: form.email } : undefined,
      });
      setCreated(r.data);
      toast.success(`Cobrança #${r.data.chargeId} criada`);
      load();
    } catch (e: any) {
      toast.error(errMsg(e, 'Erro ao criar cobrança'));
    } finally {
      setSaving(false);
    }
  };

  const consultar = async (c: Cobranca) => {
    try {
      const r = await efiApi.cobranca(c.chargeId);
      setDetail(r.data);
      setItems(prev => prev.map(i => (i.chargeId === c.chargeId ? r.data : i)));
      toast.success(`#${c.chargeId}: ${STATUS_LABEL[r.data.status]?.label || r.data.status}`);
    } catch (e: any) {
      toast.error(errMsg(e, 'Erro ao consultar'));
    }
  };

  const cancelar = async (c: Cobranca) => {
    if (!window.confirm(`Cancelar a cobrança #${c.chargeId} (${brl(c.valor)})?`)) return;
    try {
      const r = await efiApi.cancelarCobranca(c.chargeId);
      setItems(prev => prev.map(i => (i.chargeId === c.chargeId ? r.data : i)));
      toast.success('Cobrança cancelada');
    } catch (e: any) {
      toast.error(errMsg(e, 'Erro ao cancelar'));
    }
  };

  const visiveis = useMemo(() => {
    const q = filtro.trim().toLowerCase();
    return q ? items.filter(i => [i.chargeId, i.descricao, i.status, i.customer?.name, i.customer?.email].join(' ').toLowerCase().includes(q)) : items;
  }, [items, filtro]);

  const totais = useMemo(() => ({
    pagas: items.filter(i => i.pago).reduce((s, i) => s + i.valor, 0),
    abertas: items.filter(i => !i.finalizado).reduce((s, i) => s + i.valor, 0),
    nPagas: items.filter(i => i.pago).length,
    nAbertas: items.filter(i => !i.finalizado).length,
  }), [items]);

  if (account && account.provider !== 'efi') {
    return <Alert severity="info">Vendas por link de pagamento são um recurso da Efí. Selecione uma conta Efí no seletor do topo.</Alert>;
  }

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} mb={2}>
        <Box>
          <Typography variant="h5" fontWeight={600}>Vendas por link (cartão / boleto)</Typography>
          <Typography variant="body2" color="text.secondary">
            Gera um link de pagamento da Efí; o cliente paga com cartão de crédito ou boleto e a baixa chega por notificação.
            {account ? ` Conta: ${account.providerLabel} ${account.label}.` : ''}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={refreshing ? <CircularProgress size={16} /> : <Sync />} onClick={() => load(true)} disabled={refreshing}>
            Atualizar status na Efí
          </Button>
          <Button variant="contained" startIcon={<CreditCard />} onClick={() => { setCreated(null); setOpen(true); }}>
            Nova venda
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={2} mb={2}>
        {[
          { t: 'Recebido (pagas)', v: brl(totais.pagas), s: `${totais.nPagas} cobrança(s)` },
          { t: 'Em aberto', v: brl(totais.abertas), s: `${totais.nAbertas} cobrança(s)` },
          { t: 'Total de cobranças', v: String(items.length), s: 'no histórico local' },
        ].map(c => (
          <Grid item xs={12} sm={4} key={c.t}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="overline" color="text.secondary">{c.t}</Typography>
              <Typography variant="h6" fontWeight={600}>{c.v}</Typography>
              <Typography variant="caption" color="text.secondary">{c.s}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Stack direction="row" spacing={1} mb={1} alignItems="center">
        <TextField size="small" placeholder="Buscar por nº, descrição, status, cliente" value={filtro} onChange={e => setFiltro(e.target.value)} sx={{ minWidth: 320 }} />
        <IconButton size="small" onClick={() => load()}><Refresh fontSize="small" /></IconButton>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nº</TableCell>
              <TableCell>Criada</TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell>Método</TableCell>
              <TableCell align="right">Valor</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Pago em</TableCell>
              <TableCell>Validade</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={10} align="center"><CircularProgress size={18} /></TableCell></TableRow>}
            {!loading && visiveis.length === 0 && <TableRow><TableCell colSpan={10} align="center">Nenhuma cobrança</TableCell></TableRow>}
            {visiveis.map(c => {
              const st = STATUS_LABEL[c.status] || { label: c.status, color: 'default' as const };
              return (
                <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={() => setDetail(c)}>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{c.chargeId}</TableCell>
                  <TableCell>{fmt(c.createdAt)}</TableCell>
                  <TableCell>{c.descricao || '—'}</TableCell>
                  <TableCell>{c.customer?.name || c.customer?.juridical_person?.corporate_name || c.customer?.email || '—'}</TableCell>
                  <TableCell>{METODO[c.paymentMethod || ''] || c.paymentMethod || '—'}</TableCell>
                  <TableCell align="right">{brl(c.valor)}</TableCell>
                  <TableCell><Chip size="small" label={st.label} color={st.color} variant={c.pago ? 'filled' : 'outlined'} /></TableCell>
                  <TableCell>{fmt(c.paidAt)}</TableCell>
                  <TableCell>{fmt(c.expireAt)}</TableCell>
                  <TableCell align="right" onClick={e => e.stopPropagation()}>
                    {c.paymentUrl && !c.finalizado && (
                      <>
                        <Tooltip title="Copiar link"><IconButton size="small" onClick={() => copy(c.paymentUrl!)}><ContentCopy fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Abrir link"><IconButton size="small" component="a" href={c.paymentUrl} target="_blank" rel="noreferrer"><OpenInNew fontSize="small" /></IconButton></Tooltip>
                      </>
                    )}
                    <Tooltip title="Consultar na Efí"><IconButton size="small" onClick={() => consultar(c)}><Refresh fontSize="small" /></IconButton></Tooltip>
                    {!c.finalizado && <Tooltip title="Cancelar"><IconButton size="small" color="error" onClick={() => cancelar(c)}><Cancel fontSize="small" /></IconButton></Tooltip>}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Nova venda */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nova venda por link</DialogTitle>
        <DialogContent>
          {!created ? (
            <Stack spacing={2} mt={1}>
              <Stack direction="row" spacing={1}>
                <TextField size="small" label="Valor (R$)" value={form.valor} onChange={e => setForm({ ...form, valor: e.target.value })} autoFocus
                  InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
                <FormControl size="small" fullWidth>
                  <InputLabel>Forma de pagamento</InputLabel>
                  <Select label="Forma de pagamento" value={form.paymentMethod} onChange={e => setForm({ ...form, paymentMethod: e.target.value })}>
                    <MenuItem value="credit_card">Cartão de crédito</MenuItem>
                    <MenuItem value="banking_billet">Boleto</MenuItem>
                    <MenuItem value="all">Cartão ou boleto (cliente escolhe)</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              <TextField size="small" label="Descrição (aparece na página de pagamento)" value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })} inputProps={{ maxLength: 80 }} />
              <TextField size="small" label="Validade do link (dias)" value={form.validadeDias} onChange={e => setForm({ ...form, validadeDias: e.target.value })} sx={{ maxWidth: 200 }} />
              <Typography variant="caption" color="text.secondary">Cliente (opcional — se vazio, quem paga preenche na página da Efí)</Typography>
              <Stack direction="row" spacing={1}>
                <TextField size="small" label="Nome" value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} fullWidth />
                <TextField size="small" label="CPF/CNPJ" value={form.documento} onChange={e => setForm({ ...form, documento: e.target.value })} />
              </Stack>
              <TextField size="small" label="E-mail" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            </Stack>
          ) : (
            <Stack spacing={1.5} mt={1}>
              <Alert severity="success">Cobrança <b>#{created.chargeId}</b> criada — {brl(created.valor)} · {METODO[created.paymentMethod || ''] || created.paymentMethod}</Alert>
              <TextField size="small" fullWidth value={created.paymentUrl || ''} InputProps={{ readOnly: true, endAdornment: (
                <InputAdornment position="end"><IconButton onClick={() => copy(created.paymentUrl || '')}><ContentCopy /></IconButton></InputAdornment>
              ) }} />
              <Typography variant="caption" color="text.secondary">Válido até {fmt(created.expireAt)}. Quando o cliente pagar, o status muda sozinho (notificação da Efí).</Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Fechar</Button>
          {!created
            ? <Button variant="contained" onClick={create} disabled={saving}>{saving ? 'Criando…' : 'Gerar link'}</Button>
            : <Button variant="outlined" component="a" href={created.paymentUrl || '#'} target="_blank" rel="noreferrer" startIcon={<OpenInNew />}>Abrir link</Button>}
        </DialogActions>
      </Dialog>

      {/* Detalhe */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Cobrança #{detail?.chargeId}</DialogTitle>
        <DialogContent>
          {detail && (
            <Stack spacing={1} mt={1}>
              <Typography variant="body2"><b>Valor:</b> {brl(detail.valor)} · <b>Status:</b> {STATUS_LABEL[detail.status]?.label || detail.status} · <b>Método:</b> {METODO[detail.paymentMethod || ''] || detail.paymentMethod || '—'}</Typography>
              <Typography variant="body2"><b>Descrição:</b> {detail.descricao || '—'}</Typography>
              <Typography variant="body2"><b>Criada:</b> {fmt(detail.createdAt)} · <b>Validade:</b> {fmt(detail.expireAt)} · <b>Paga em:</b> {fmt(detail.paidAt)}</Typography>
              {detail.payment && <Typography variant="body2"><b>Pagamento:</b> {JSON.stringify(detail.payment)}</Typography>}
              {detail.paymentUrl && <TextField size="small" fullWidth value={detail.paymentUrl} InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><IconButton onClick={() => copy(detail.paymentUrl || '')}><ContentCopy /></IconButton></InputAdornment> }} />}
              <Typography variant="subtitle2" mt={1}>Notificações recebidas</Typography>
              {detail.history.length === 0 ? <Typography variant="caption" color="text.secondary">Nenhuma ainda</Typography> : (
                <Table size="small">
                  <TableHead><TableRow><TableCell>Quando</TableCell><TableCell>De</TableCell><TableCell>Para</TableCell></TableRow></TableHead>
                  <TableBody>{detail.history.map(h => (
                    <TableRow key={h.id}><TableCell>{h.created_at}</TableCell><TableCell>{h.previous || '—'}</TableCell><TableCell>{h.current}</TableCell></TableRow>
                  ))}</TableBody>
                </Table>
              )}
              <Typography variant="caption" color="text.secondary">Webhook de notificação: {detail.notificationUrl ? 'configurado' : 'não configurado'}</Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {detail && <Button onClick={() => consultar(detail)} startIcon={<Refresh />}>Consultar na Efí</Button>}
          <Button onClick={() => setDetail(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EfiCobrancasPage;
